import React, { useMemo, useState } from 'react';
import {
    SchemaGroup,
    SchemaGroupField,
    upsertDeviceExtValue,
} from '../../api/schema';
import { alertApprovalIfPending, ApiError } from '../../api/request';
import GroupedFields from '../../components/GroupedFields';
import SearchSelect from '../../components/SearchSelect';
import HelpTip from '../../components/HelpTip';
import { MatrixParams, MatrixRow } from '../../components/MatrixGroupEditor';
import Modal from '../../components/Modal';
import { alertDialog, confirmDialog } from '../../utils/dialog';
import { Layout } from '../../utils/fieldGroups';
import {
    DictOptions,
    SchemaFieldInput,
    buildExtPatch,
    extValueToInput,
    schemaFieldText,
} from './deviceSchemaForm';

// 有分组布局的矩阵字段组 (2026-10-23, 如 FDM 工艺预设): 「选一行 → 页签 → 分组」看这一行的整套值,
// 编辑也只改当前行。行来自字典 (如「FDM 工艺预设」, 全局一份、不绑定设备) 时为「按设备挑行」:
// 设备只显示自己用到 (存了值) 的那几个, 在这里从字典挑一个加进来 / 移除, 都是改这台设备的值 (一张工单)。
// 没有布局的矩阵组 (激光模式能力) 仍走 MatrixGroupEditor 表格。

function errMsg(e: unknown): string {
    return e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);
}

// 选中行按 设备+组 记在模块级: 保存后父级重取详情时本组件会随加载态卸载重建, 组件内 state 留不住选中
const pickedRows = new Map<string, string>();

const fieldAttrs = (f: SchemaGroupField) => f;
const fieldKey = (f: SchemaGroupField) => f.key;

export default function LayoutMatrixGroup({
    deviceKey,
    group,
    fields,
    layout,
    rows,
    params,
    dictOpts,
    canUpdate,
    onSaved,
}: {
    deviceKey: string;
    group: SchemaGroup;
    fields: SchemaGroupField[];
    layout: Layout;
    rows: MatrixRow[];
    params: MatrixParams | null | undefined;
    dictOpts: DictOptions;
    canUpdate: boolean;
    onSaved: () => Promise<void> | void;
}) {
    const values = useMemo(
        () => (params || {}) as Record<string, Record<string, unknown>>,
        [params],
    );
    // 按设备挑行 (与后端 DeviceSchema.PicksRows 同口径: 有布局 + 行来自字典; 加工模式组行照旧列全部)
    const preset = group.rowSource !== 'mm_group';
    // 只列设备用到的, 顺序随字典; 字典里已停用 / 不在列表的行仍显示 (值还在), 名称回落编码
    const shown = useMemo(() => {
        if (!preset) return rows;
        const known = rows.filter((r) => r.code in values);
        const extra = Object.keys(values)
            .filter((k) => !rows.some((r) => r.code === k))
            .map((k) => ({ code: k, label: k }));
        return [...known, ...extra];
    }, [preset, rows, values]);
    const addable = useMemo(
        () => rows.filter((r) => !(r.code in values)),
        [rows, values],
    );
    // 用户点选 / 刚添加的行; 当前行由它推导 —— 不在列表里 (未选过 / 已移除 / 刚添加还没刷新到) 时回落第一行.
    // 2026-10-24 前用 effect 回写 cur: 添加后先 setCur 再刷新, effect 在新行到达前就把选中重置回第一行,
    // 接着点「编辑」改的是别的预设; 首屏字典晚于设备值到达时也会停在字母序最前的预设上.
    const pickKey = `${deviceKey}|${group.groupKey}`;
    const [picked, setPickedState] = useState(() => pickedRows.get(pickKey) || '');
    const setPicked = (code: string) => {
        pickedRows.set(pickKey, code);
        setPickedState(code);
    };
    const cur = shown.some((r) => r.code === picked)
        ? picked
        : shown[0]?.code || '';
    const [edit, setEdit] = useState<Record<string, string> | null>(null);
    const [busy, setBusy] = useState(false);
    const [adding, setAdding] = useState<{
        code: string;
        copyFrom: string;
    } | null>(null);
    const rowVals = useMemo(() => values[cur] || {}, [values, cur]);
    const filled = useMemo(
        () =>
            fields.filter(
                (f) => rowVals[f.key] != null && rowVals[f.key] !== '',
            ).length,
        [fields, rowVals],
    );
    const rowName = group.rowLabel || '行';
    const labelOf = (code: string) =>
        rows.find((r) => r.code === code)?.label || code;

    const save = async (next: Record<string, unknown>, after?: () => void) => {
        setBusy(true);
        try {
            const res = await upsertDeviceExtValue(
                deviceKey,
                group.groupKey,
                JSON.stringify(next),
            );
            after?.();
            await onSaved();
            alertApprovalIfPending(res.message);
        } catch (e) {
            alertDialog(`保存失败: ${errMsg(e)}`);
        } finally {
            setBusy(false);
        }
    };

    const startEdit = () => {
        const next: Record<string, string> = {};
        for (const f of fields) next[f.key] = extValueToInput(rowVals[f.key]);
        setEdit(next);
    };

    const saveRow = async () => {
        if (!edit) return;
        const { extPatch, invalid } = buildExtPatch(fields, edit);
        if (invalid) {
            alertDialog(
                `「${invalid.field.label}」不是有效数字: ${invalid.raw}`,
            );
            return;
        }
        await save({ ...values, [cur]: extPatch }, () => setEdit(null));
    };

    // 添加预设: 新行以空白或某个已用预设的整套值为起点
    const addRow = async () => {
        if (!adding?.code) return;
        const code = adding.code;
        await save(
            {
                ...values,
                [code]: adding.copyFrom ? { ...values[adding.copyFrom] } : {},
            },
            () => {
                setAdding(null);
                setPicked(code);
            },
        );
    };

    const removeRow = async () => {
        if (
            !cur ||
            !(await confirmDialog(
                `确认从本设备移除${rowName}「${labelOf(cur)}」及它的全部值?`,
            ))
        )
            return;
        const next = { ...values };
        delete next[cur];
        await save(next);
    };

    const renderField = (f: SchemaGroupField) => {
        if (edit) {
            return (
                <div>
                    <label className="text-xs text-slate-500 mb-1 block">
                        {f.label}
                        {f.unit ? ` (${f.unit})` : ''}
                    </label>
                    <SchemaFieldInput
                        field={f}
                        value={edit[f.key] ?? ''}
                        onChange={(v) => setEdit({ ...edit, [f.key]: v })}
                        dictOpts={dictOpts}
                    />
                </div>
            );
        }
        const v = rowVals[f.key];
        const empty = v == null || v === '';
        return (
            <div className="flex justify-between text-xs border-b border-slate-50 py-1">
                <span className="text-slate-400">
                    {f.label}
                    {f.unit && (
                        <span className="text-slate-300"> ({f.unit})</span>
                    )}
                </span>
                <span
                    className={
                        empty ? 'text-slate-300' : 'text-slate-700 font-mono'
                    }
                >
                    {empty ? '—' : schemaFieldText(f, v, dictOpts)}
                </span>
            </div>
        );
    };

    return (
        <div>
            <div className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-2 flex-wrap">
                {group.label}
                {group.src && (
                    <HelpTip
                        text={
                            <>
                                数据来源: <code>{group.src}</code>
                            </>
                        }
                    />
                )}
                {shown.length > 0 && (
                    <SearchSelect
                        className="w-72 font-normal"
                        value={cur}
                        onChange={(v) => {
                            if (!edit) setPicked(v);
                        }}
                        disabled={!!edit}
                        options={shown.map((r) => ({
                            value: r.code,
                            label: r.label,
                        }))}
                        placeholder={`选择${rowName}`}
                    />
                )}
                <span className="text-[11px] text-slate-400 font-normal">
                    共 {shown.length} 个{rowName}
                    {cur
                        ? ` · 本${rowName}已填 ${filled}/${fields.length}`
                        : ''}
                </span>
                {canUpdate && !edit && (
                    <span className="ml-auto flex items-center gap-3 font-normal">
                        {cur && (
                            <button
                                type="button"
                                className="text-xs text-brand-600 hover:underline"
                                disabled={busy}
                                onClick={startEdit}
                            >
                                ✎ 编辑
                            </button>
                        )}
                        {preset && (
                            <>
                                <button
                                    type="button"
                                    className="text-xs text-brand-600 hover:underline disabled:opacity-40"
                                    disabled={busy || addable.length === 0}
                                    title={
                                        addable.length === 0
                                            ? `字典里的${rowName}都已添加 (条目在「数据字典 → 字典维护」维护)`
                                            : ''
                                    }
                                    onClick={() =>
                                        setAdding({ code: '', copyFrom: '' })
                                    }
                                >
                                    + 添加{rowName}
                                </button>
                                {cur && (
                                    <button
                                        type="button"
                                        className="text-xs text-rose-500 hover:underline"
                                        disabled={busy}
                                        onClick={removeRow}
                                    >
                                        移除
                                    </button>
                                )}
                            </>
                        )}
                    </span>
                )}
            </div>
            {shown.length === 0 ? (
                <div className="text-xs text-slate-400 py-3">
                    {preset
                        ? `这台设备还没有${rowName}${canUpdate ? `, 点「+ 添加${rowName}」从字典里选` : ''}`
                        : `行来源「${group.rowSource}」暂无条目`}
                </div>
            ) : (
                <GroupedFields
                    items={fields}
                    attrs={fieldAttrs}
                    layout={layout}
                    itemKey={fieldKey}
                    renderItem={renderField}
                    gridClassName="grid grid-cols-3 gap-x-4 gap-y-1"
                    collapsible
                    showEmpty={!!edit}
                />
            )}
            {edit && (
                <div className="flex justify-end gap-2 mt-2">
                    <button
                        type="button"
                        className="btn-ghost text-xs"
                        disabled={busy}
                        onClick={() => setEdit(null)}
                    >
                        取消
                    </button>
                    <button
                        type="button"
                        className="btn-primary text-xs"
                        disabled={busy}
                        onClick={saveRow}
                    >
                        {busy ? '保存中…' : `保存本${rowName}`}
                    </button>
                </div>
            )}
            <Modal
                open={!!adding}
                title={`添加${rowName}`}
                width={480}
                onClose={() => setAdding(null)}
            >
                {adding && (
                    <div className="space-y-4">
                        <div>
                            <label className="text-xs text-slate-500 block mb-1">
                                {rowName}
                            </label>
                            <SearchSelect
                                className="w-full"
                                value={adding.code}
                                onChange={(v) =>
                                    setAdding({ ...adding, code: v })
                                }
                                options={addable.map((r) => ({
                                    value: r.code,
                                    label: r.label,
                                }))}
                                placeholder={`从字典选择${rowName}`}
                            />
                        </div>
                        <div>
                            <label className="text-xs text-slate-500 block mb-1">
                                初始值
                            </label>
                            <SearchSelect
                                className="w-full"
                                value={adding.copyFrom}
                                onChange={(v) =>
                                    setAdding({ ...adding, copyFrom: v })
                                }
                                options={[
                                    { value: '', label: '空白' },
                                    ...shown.map((r) => ({
                                        value: r.code,
                                        label: `复制「${r.label}」的值`,
                                    })),
                                ]}
                            />
                        </div>
                        <div className="text-[11px] text-slate-400">
                            条目在「数据字典 → 字典维护」维护;
                            这里只决定本设备用哪些并填值。
                        </div>
                        <div className="flex justify-end gap-2">
                            <button
                                type="button"
                                className="btn-ghost"
                                disabled={busy}
                                onClick={() => setAdding(null)}
                            >
                                取消
                            </button>
                            <button
                                type="button"
                                className="btn-primary"
                                disabled={busy || !adding.code}
                                onClick={addRow}
                            >
                                {busy ? '保存中…' : '添加'}
                            </button>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
}
